<template>
  <apexchart ref="chart" height="100%" :options="chartOptions" :series="chartSeries"></apexchart>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { formatCurrencyUSD } from '@/utils/funcs.js';
import {
  AMPLITUDE_SERIES,
  OPEN_CHART_ID,
  OPEN_LONG_PURPOSES,
  OPEN_PURPOSES,
  SIBLING_PURPOSES,
  VOL_PER_AMP_SERIES,
  axisMaxFromValues,
  buildRightAxisYAxes,
  categoryXAxisOptions,
  extractLogDay,
  hideZeroDataLabel,
  manualChartPanOptions,
  readLogAmplitude,
  readOrderNotional,
  readOrderQty,
  rightAxisMetricMixin,
  volumePerAmplitude,
} from '@/utils/manualChartDays.js';

export default {
  name: 'EvolManualOpenChart',
  mixins: [rightAxisMetricMixin],
  props: {
    logs: { type: Array, default: () => [] },
    rawData: { type: Array, default: () => [] },
    aggregatedData: { type: Array, default: () => [] },
  },
  computed: {
    allLogs() {
      if (this.logs && this.logs.length > 0) return this.logs;
      if (this.rawData && this.rawData.length > 0) return this.rawData;
      if (this.aggregatedData && this.aggregatedData.length > 0) return this.aggregatedData;
      return this.$store?.state?.eventLogs || [];
    },

    dailyBuckets() {
      const dayMap = new Map();

      this.allLogs.forEach(log => {
        if (log.processed === false) return;
        if (!SIBLING_PURPOSES.has(log.purpose)) return;

        const day = extractLogDay(log);
        if (!day) return;

        if (!dayMap.has(day.dayKey)) {
          dayMap.set(day.dayKey, {
            dayKey: day.dayKey,
            label: day.label,
            openLongUSD: 0,
            openShortUSD: 0,
            openLongQty: 0,
            openShortQty: 0,
            openLongCount: 0,
            openShortCount: 0,
            amplitude: null,
            volPerAmp: null,
          });
        }

        const bucket = dayMap.get(day.dayKey);
        const amplitude = readLogAmplitude(log);
        if (bucket.amplitude == null && amplitude != null) {
          bucket.amplitude = amplitude;
        }

        if (!OPEN_PURPOSES.has(log.purpose)) return;

        const volumeUSD = readOrderNotional(log);
        const qty = readOrderQty(log);

        if (OPEN_LONG_PURPOSES.has(log.purpose)) {
          bucket.openLongUSD += volumeUSD;
          bucket.openLongQty += qty;
          bucket.openLongCount += 1;
        } else {
          bucket.openShortUSD += volumeUSD;
          bucket.openShortQty += qty;
          bucket.openShortCount += 1;
        }
      });

      const sorted = Array.from(dayMap.values()).sort((a, b) => a.dayKey.localeCompare(b.dayKey));
      sorted.forEach((bucket) => {
        bucket.volPerAmp = volumePerAmplitude(
          bucket.openLongUSD + bucket.openShortUSD,
          bucket.amplitude
        );
      });

      return {
        labels: sorted.map(d => d.label),
        openLongUSD: sorted.map(d => parseFloat(d.openLongUSD.toFixed(2))),
        openShortUSD: sorted.map(d => parseFloat(d.openShortUSD.toFixed(2))),
        amplitude: sorted.map(d => (d.amplitude == null ? null : parseFloat(d.amplitude.toFixed(3)))),
        volPerAmp: sorted.map(d => d.volPerAmp),
        rawBuckets: sorted,
      };
    },

    chartSeries() {
      return [
        { name: 'Open Long', type: 'bar', data: this.dailyBuckets.openLongUSD },
        { name: 'Open Short', type: 'bar', data: this.dailyBuckets.openShortUSD },
        {
          name: AMPLITUDE_SERIES,
          type: 'area',
          data: this.dailyBuckets.amplitude,
          hidden: this.rightAxisMetric !== 'amplitude',
        },
        {
          name: VOL_PER_AMP_SERIES,
          type: 'area',
          data: this.dailyBuckets.volPerAmp,
          hidden: this.rightAxisMetric !== 'volPerAmp',
        },
      ];
    },

    amplitudeAxisMax() {
      return axisMaxFromValues(this.dailyBuckets.amplitude, 5);
    },

    volPerAmpAxisMax() {
      return axisMaxFromValues(this.dailyBuckets.volPerAmp, 100);
    },

    chartOptions() {
      const self = this;
      return {
        ...defaultChartOptions,
        chart: {
          ...defaultChartOptions.chart,
          ...manualChartPanOptions(),
          id: OPEN_CHART_ID,
          type: 'bar',
          stacked: true,
          stackOnlyBar: true,
          events: {
            mounted(chartContext) {
              self.syncRightAxisSeries(chartContext);
              self.applyLatestXWindow(chartContext);
            },
            legendClick(chartContext, seriesIndex, opts) {
              self.handleRightAxisLegendClick(chartContext, seriesIndex, opts);
            },
            beforeResetZoom() {
              return self.latestResetZoomWindow();
            },
            zoomed(chartContext, opts) {
              self.onSiblingXWindow(chartContext, opts);
            },
            scrolled(chartContext, opts) {
              self.onSiblingXWindow(chartContext, opts);
            },
          },
        },
        title: {
          text: 'Abertura de Posição',
          align: 'left',
          style: { color: '#f1f1f1', fontSize: '14px', fontWeight: 600 },
        },
        colors: ['#22c55e', '#c084fc', '#fbbf24', '#2dd4bf'],
        fill: { opacity: [0.85, 0.85, 0.35, 0.35] },
        stroke: { curve: 'smooth', width: [0, 0, 2, 2] },
        markers: { size: [0, 0, 3, 3], hover: { size: 4 }, strokeWidth: 0 },
        xaxis: categoryXAxisOptions(this.dailyBuckets.labels),
        yaxis: [
          {
            seriesName: 'Open Long',
            title: { text: 'Aberto Diário (USD)' },
            labels: { formatter: formatCurrencyUSD },
          },
          {
            seriesName: 'Open Long',
            show: false,
          },
          ...buildRightAxisYAxes(this.rightAxisMetric, this.amplitudeAxisMax, this.volPerAmpAxisMax),
        ],
        dataLabels: {
          enabled: true,
          enabledOnSeries: [0, 1],
          formatter: hideZeroDataLabel,
        },
        plotOptions: {
          bar: {
            columnWidth: '60%',
            distributed: false,
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: function({ dataPointIndex }) {
            return self.generateTooltipHtml(dataPointIndex);
          },
        },
      };
    },
  },
  methods: {
    generateTooltipHtml(dataPointIndex) {
      const bucket = this.dailyBuckets.rawBuckets[dataPointIndex];
      if (!bucket) return '';

      const fmtUSD = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const fmtBTC = (v) => `${(v || 0).toFixed(4)} BTC`;
      const fmtAvgQty = (qty, count) => {
        if (!count) return '';
        return ` | média ${fmtBTC(qty / count)}`;
      };

      const totalUSD = bucket.openLongUSD + bucket.openShortUSD;
      const totalQty = bucket.openLongQty + bucket.openShortQty;
      const totalCount = bucket.openLongCount + bucket.openShortCount;

      return `
        <div style="background-color: #1e1e1e; color: #fff; padding: 10px 14px; border-radius: 6px; border: 1px solid #444; font-size: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); min-width: 250px;">
          <div style="font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px; color: #c084fc;">
            📅 Data: ${bucket.label}
          </div>
          <div style="margin-bottom: 8px;">
            <div style="color: #22c55e; font-weight: bold;">🟢 Open Long:</div>
            <div style="padding-left: 10px; line-height: 1.5;">
              Volume: <b>${fmtUSD(bucket.openLongUSD)}</b><br/>
              Qtd: <b>${fmtBTC(bucket.openLongQty)}</b> (${bucket.openLongCount} ordens${fmtAvgQty(bucket.openLongQty, bucket.openLongCount)})
            </div>
          </div>
          <div style="margin-bottom: 8px;">
            <div style="color: #c084fc; font-weight: bold;">🟣 Open Short:</div>
            <div style="padding-left: 10px; line-height: 1.5;">
              Volume: <b>${fmtUSD(bucket.openShortUSD)}</b><br/>
              Qtd: <b>${fmtBTC(bucket.openShortQty)}</b> (${bucket.openShortCount} ordens${fmtAvgQty(bucket.openShortQty, bucket.openShortCount)})
            </div>
          </div>
          <div style="border-top: 1px solid #444; padding-top: 6px; color: #f1f1f1;">
            Total: <b>${fmtUSD(totalUSD)}</b> | <b>${fmtBTC(totalQty)}</b> | ${totalCount} ordens
            ${bucket.amplitude != null ? `<br/>Amplitude: <b style="color: #fbbf24">${bucket.amplitude.toFixed(2)}%</b>` : ''}
            ${bucket.volPerAmp != null ? `<br/>Vol / Amp: <b style="color: #2dd4bf">${fmtUSD(bucket.volPerAmp)}</b>` : ''}
          </div>
        </div>
      `;
    },
  },
};
</script>
